import type { ProjectListing } from '@/api/v4/projects.api';
import { formatEmpty } from '@/helpers/format-empty';
import { formatLocation } from '@/helpers/format-location';
import { paths } from '@/routes/helpers/paths';
import { colors } from '@/theme/colors';
import { theme } from '@/theme/setupTheme';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';
import { LinkIcon } from '@/ui/link/link-icon';
import { Logo } from '@/ui/logo/logo';
import { ParagraphSmall, SmallText } from '@/ui/typography/widgets';
import { styled } from 'goober';
import { forwardRef } from 'react';
import { Tooltip } from '@/ui/tooltip/tooltip';
import { Link } from '@/ui/link/link';

export const CompanyNameTableCell = ({
  listing,
  shouldLinkToCompany,
}: {
  listing: ProjectListing;
  shouldLinkToCompany: boolean;
}) => {
  const hqLocation = listing.organization.hqLocation
    ? {
        cityName: listing.organization.hqLocation.city?.name,
        regionName: listing.organization.hqLocation.region?.name,
        countryName: listing.organization.hqLocation.region?.name,
      }
    : null;
  return (
    <Wrapper name="company-name-container" justify="space-between" gap="8px">
      <Flexbox gap="8px" alignItems="center">
        <Logo
          singleLetter
          name={listing.organization.name}
          logoUrl={listing.organization.logoUrl ?? undefined}
          bgColor={colors.accent.green.c3}
          border={
            listing.organization.logoUrl
              ? `2px solid ${colors.gray.c3}`
              : undefined
          }
        />
        <Stack gap="3px">
          <Flexbox name="discovery-company-name" alignItems="center" gap="2px">
            <Tooltip
              content={listing.organization.keyOffering}
              visible={!!listing.organization.keyOffering}
              maxWidth="500px"
              placement="top-start"
            >
              {shouldLinkToCompany ? (
                <Link
                  to={paths.company({ companyId: listing.organization.id })}
                  color={theme.colors.basics.black}
                  weight="600"
                  withEllipsis
                  maxWidth="150px"
                >
                  {listing.organization.name}
                </Link>
              ) : (
                <CompanyName>{listing.organization.name}</CompanyName>
              )}
            </Tooltip>
          </Flexbox>

          <Tooltip
            disabled={!listing.organization.hqLocation?.city}
            content={formatEmpty(hqLocation && formatLocation(hqLocation))}
          >
            <Location isNotRelevant={listing.notRelevant}>
              {formatEmpty(
                hqLocation &&
                  formatLocation(hqLocation, {
                    compact: true,
                  }),
              )}
            </Location>
          </Tooltip>
        </Stack>
      </Flexbox>
      <Flexbox name="search-result-urls" alignItems="center">
        {!!listing.organization.website && (
          <StyledLinkIcon
            url={listing.organization.website}
            icon="GlobeBlue"
            stopPropagation
            needRightSpacing={!listing.organization.linkedInUrl}
          />
        )}
        {!!listing.organization.linkedInUrl && (
          <StyledLinkIcon
            icon="LinkedIn"
            url={listing.organization.linkedInUrl}
            size="16px"
            color={colors.blue.primaryA}
            stopPropagation
          />
        )}
      </Flexbox>
    </Wrapper>
  );
};

const Wrapper = styled(Flexbox)`
  max-width: 300px;
  font-family: ${theme.fonts.inter};
`;

const CompanyName = styled(ParagraphSmall, forwardRef)`
  max-width: 173px;
  min-height: 20px;
  font-weight: 600;
  ${({ theme }) => theme.mixins.ellipsis};
`;

const Location = styled(SmallText, forwardRef)<{ isNotRelevant?: boolean }>`
  max-width: 173px;
  ${({ isNotRelevant, theme }) =>
    `color: ${isNotRelevant ? theme.colors.gray.c7 : undefined}`};
  ${({ theme }) => theme.mixins.ellipsis};
`;

const StyledLinkIcon = styled(LinkIcon)<{
  needRightSpacing?: boolean;
}>`
  display: flex;
  align-items: center;
  margin-left: 5px;
  ${({ needRightSpacing }) => needRightSpacing && 'margin-right: 21.5px;'}
`;
